import type { ActivityTimeseriesPoint } from "@/lib/api";

/**
 * Dependency-free bar chart of chat messages per day — the measure of whether
 * the tool is being USED, as opposed to the login count this replaced, which
 * only showed that someone opened it.
 *
 * One series, labelled. The chart previously stacked chat messages inside a
 * lighter total-events bar, which needed a legend to be readable at all; with
 * the count printed over each bar the second series bought nothing a reader
 * could act on, and the total still has its own card. Events remain in the
 * hover tooltip for context.
 *
 * Counts exclude internal-domain accounts (see `internal_email_domains`), so
 * they are deliberately lower than the summary cards above the chart, which
 * count everyone.
 */
export function ActivityChart({ data }: { data: ActivityTimeseriesPoint[] }) {
  const max = Math.max(1, ...data.map((d) => d.chat_messages));

  return (
    <div className="flex h-40 items-stretch gap-1">
      {data.map((point) => (
        <div key={point.day} className="group relative flex flex-1 flex-col justify-end">
          {/* Zeros are left blank: a '0' over every quiet day is noise, and the
              stub bar beneath already shows the day exists. */}
          <span className="text-muted mb-0.5 text-center text-[9px] leading-none tabular-nums">
            {point.chat_messages || ""}
          </span>
          <div
            className="bg-accent w-full rounded-t"
            style={{ height: `${Math.max(2, (point.chat_messages / max) * 100)}%` }}
          />
          <span className="pointer-events-none absolute -top-7 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-gray-900 px-1.5 py-0.5 text-[10px] whitespace-nowrap text-white group-hover:block">
            {point.day}: {point.chat_messages} chat messages · {point.events} events
          </span>
        </div>
      ))}
    </div>
  );
}
